/**
 * WelcomeStarterChips
 *
 * F4-A (Malaika UX series 2026-07-13): the four "Try one of these to get
 * started:" prompts used to render only as static `<li>` bullets inside the
 * welcome bubble — users had to copy/paste them. This renders them as one-click
 * chips under the welcome bubble on an EMPTY thread; clicking one sends the
 * exact prompt text as a normal first user message (via sendProgrammaticMessage).
 *
 * Native `<button>` chips → focusable + keyboard-activatable (Enter/Space) for
 * free. The row disappears once the thread has a real user message (same
 * empty-state lifecycle as the welcome bubble — F4-B).
 */

import React from 'react';
import { StarterChipRow, StarterChip } from './styles';

/** Minimal message shape this module needs (avoids importing the full ChatMessage). */
interface StarterGateMessage {
  id?: string;
  isUser?: boolean;
  sourceTag?: string;
}

/**
 * Pure predicate: show the starter chips ONLY on an empty-thread welcome state
 * — the thread has the injected welcome bubble, no real user message yet, we
 * have starters to show, and no turn is in flight. Extracted for unit testing
 * without mounting GlobalChatSystem.
 */
export function shouldShowStarterChips(params: {
  messages: ReadonlyArray<StarterGateMessage> | undefined;
  starters: readonly string[] | undefined;
  isProcessing: boolean;
}): boolean {
  const { messages, starters, isProcessing } = params;
  if (isProcessing) return false;
  if (!starters || starters.length === 0) return false;
  if (!Array.isArray(messages) || messages.length === 0) return false;
  const hasWelcome = messages.some((m) => m?.id === 'welcome' || m?.sourceTag === 'welcome');
  if (!hasWelcome) return false;
  const hasUserMessage = messages.some((m) => m?.isUser === true);
  return !hasUserMessage;
}

export const WelcomeStarterChips: React.FC<{
  starters: readonly string[];
  /** Called with the EXACT prompt text (no display quotes) when a chip is picked. */
  onPick: (text: string) => void;
  disabled?: boolean;
}> = ({ starters, onPick, disabled }) => {
  if (!starters || starters.length === 0) return null;
  return (
    <StarterChipRow
      role="group"
      aria-label="Suggested starter prompts"
      data-testid="welcome-starter-chips"
    >
      {starters.map((text, i) => (
        <StarterChip
          key={`${i}:${text}`}
          type="button"
          disabled={disabled}
          title={text}
          data-starter-prompt={text}
          onClick={() => {
            if (!disabled) onPick(text);
          }}
        >
          {text}
        </StarterChip>
      ))}
    </StarterChipRow>
  );
};
